<!-- @format -->

<template>
  <div>
    <div class="loading loading--sm" v-if="!isLoaded">
      <div class="double-bounce1"></div>
      <div class="double-bounce2"></div>
    </div>
    <div class="background-white container main-container" v-else>
      <h1 class="h1">{{ 'Herd Characteristics' | translate }}</h1>
      <div class="card mt-4 mb-0">
        <div class="card-body">
          <div class="card-body__top d-flex justify-content-end">
            <p>* {{ 'Field Required' | translate }}</p>
          </div>

          <div class="row">
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Evaluation Name' | translate }}
                  <span class="required">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  name="evaluation_name"
                  v-model="evaluation.name"
                  data-vv-as="Evaluation Name"
                  v-validate="'required'"
                />
                <span v-show="errors.has('evaluation_name')" class="text-danger">
                  <small>{{ 'The Evaluation Name field is required.' | translate }}</small>
                </span>
              </div>
            </div>
          </div>

          <hr class="mt-2" />

          <h2 class="h2">{{ 'Company' | translate }}</h2>
          <div class="row">
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Company' | translate }}
                  <span class="required">*</span>
                </label>
                <!-- Show company name in read-only mode if user is not owner -->
                <input
                  v-if="!isOwner && client && client.name"
                  type="text"
                  class="form-control"
                  :value="client.name"
                  readonly
                />
                <!-- Show loading indicator while the companies list is being fetched -->
                <div v-else-if="clientsLoading" class="loading loading--sm">
                  <div class="double-bounce1"></div>
                  <div class="double-bounce2"></div>
                </div>
                <!-- Show select dropdown if user is owner -->
                <select
                  v-else
                  :disabled="!isOwner"
                  class="form-control"
                  v-model="evaluation.clientId"
                  @change="changeClient()"
                  name="company"
                  data-vv-as="Company"
                  v-validate="'required'"
                >
                  <option disabled :value="null">
                    {{ 'Select' | translate }}
                  </option>
                  <option @selected="newClient" :value="recordSelection.MANUAL">
                    {{ 'Add New Company' | translate }}
                  </option>
                  <option
                    v-for="(clientSelect, i) in squadClients"
                    :key="clientSelect._id + i"
                    :value="clientSelect._id"
                  >
                    {{ clientSelect.name }}
                  </option>
                </select>
                <span v-show="isOwner && errors.has('company')" class="text-danger">
                  <small>{{ 'The Company field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-12">
              <div class="form-group" v-if="evaluation.clientId == recordSelection.MANUAL">
                <label class="mb-1"
                  >{{ 'Company Name' | translate }}
                  <span class="required">*</span>
                </label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  name="company_name"
                  v-model="clientName"
                  data-vv-as="Company Name"
                  v-validate="'required'"
                />
                <span v-show="isOwner && errors.has('company_name')" class="text-danger">
                  <small>{{ 'The Company Name field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Address 1' | translate }}
                  <span class="required">*</span>
                </label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  name="company_address_line_1"
                  v-model="clientAddress"
                  data-vv-as="Address 1"
                  v-validate="'required'"
                />
                <span v-show="isOwner && errors.has('company_address_line_1')" class="text-danger">
                  <small>{{ 'The Address 1 field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Country' | translate }}
                  <span class="required">*</span>
                </label>
                <select
                  v-if="client"
                  :disabled="!isOwner"
                  @change="client.state = null"
                  class="form-control"
                  v-model="(client || {}).country"
                  name="company_country"
                  data-vv-as="Country"
                  v-validate="'required'"
                >
                  <option :value="null" disabled>
                    {{ 'Select' | translate }}
                  </option>
                  <option
                    v-for="(country, index) in $store.state.misc.countriesPrimary"
                    :key="index"
                    :value="index"
                  >
                    {{ country | translate }}
                  </option>
                  <option disabled>------------------</option>
                  <option
                    v-for="(country, index) in $store.state.misc.countries"
                    :key="country"
                    :value="index"
                  >
                    {{ country | translate }}
                  </option>
                </select>
                <span v-show="isOwner && errors.has('company_country')" class="text-danger">
                  <small>{{ 'The Country field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div v-if="client" class="form-group">
                <label class="mb-1">
                  {{ 'State/Province' | translate }}
                </label>
                <select
                  v-if="
                    misc.states[client.country] && Object.keys(misc.states[client.country]).length
                  "
                  :disabled="!isOwner"
                  class="form-control"
                  v-model="client.state"
                >
                  <option :value="null" disabled>
                    {{ 'Select' | translate }}
                  </option>
                  <template
                    v-if="
                      misc.states[client.country] &&
                      misc.states[client.country].primary &&
                      Object.keys(misc.states[client.country].primary).length
                    "
                  >
                    <option
                      v-for="(state, abbr) in misc.states[client.country].primary"
                      :key="abbr"
                      :value="abbr"
                    >
                      {{ state }}
                    </option>
                    <option disabled>------------------</option>
                  </template>

                  <option
                    v-for="(state, abbr) in misc.states[client.country].regular"
                    :key="state"
                    :value="abbr"
                  >
                    {{ state }}
                  </option>
                </select>
                <input
                  v-else
                  v-model="client.state"
                  type="text"
                  class="form-control"
                  :readonly="!isOwner"
                />
              </div>
            </div>
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1">{{ 'Address 2' | translate }}</label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  v-model="(client || {}).address_2"
                />
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'City' | translate }}
                  <span class="required">*</span>
                </label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  v-model="clientCity"
                  name="company_city"
                  data-vv-as="City"
                  v-validate="'required'"
                />
                <span v-show="isOwner && errors.has('company_city')" class="text-danger">
                  <small>{{ 'The City field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1">{{ 'Postal Code' | translate }} </label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  v-model="clientPostal"
                  name="company_postal_code"
                  data-vv-as="Postal Code"
                />
                <!-- pattern="\d{5}-?(\d{4})?"  -->
                <span v-show="isOwner && errors.has('company_postal_code')" class="text-danger">
                  <small>{{ 'The Postal Code field is required.' | translate }}</small>
                </span>
              </div>
            </div>
          </div>
          <div v-if="client">
            <div class="card-body__top">
              <h2 class="h2 card-body__top__h3">
                {{ 'Branding Image' | translate }}
              </h2>
            </div>
            <div class="row card-section">
              <div class="col-md-12">
                <p v-if="!client.brandingImageBase64">
                  {{
                    'You can add an image to be included on all reports you generate.' | translate
                  }}
                </p>
                <div class="brandingImg">
                  <img
                    v-if="client.brandingImageBase64"
                    class="w-100 mb-3 mb-md-0"
                    :src="
                      client.brandingImageBase64.startsWith('data:image') &&
                      dataURItoBlob(client.brandingImageBase64)
                    "
                    alt=""
                    ref="brandingImageBase64"
                  />
                  <i
                    v-if="client.brandingImageBase64 && isOwner"
                    class="fa fa-close"
                    @click="() => removeBrandingImage('client')"
                  ></i>
                </div>
              </div>
              <div class="col-md-12">
                <label
                  for="upload-photo"
                  class="btn btn-primary btn-full--sm"
                  :class="{ disabled: !isOwner }"
                  >{{ 'Upload Image' | translate }}</label
                >
                <input
                  ref="fileInput"
                  class=""
                  id="upload-photo"
                  @change="(e) => fileUpload(e, 'client')"
                  type="file"
                  name="pic"
                  accept=".jpg, .png, .heif"
                  :disabled="!isOwner"
                />
                <p class="p--inline">
                  {{ 'Max upload size: 5MB (JPG, PNG, HEIF)' | translate }}
                </p>
                <p style="color: red" v-if="fileSizeError">
                  {{ 'This file is too large, must be less than 5MB' | translate }}
                </p>
                <p style="color: red" v-if="fileFormatError">
                  {{
                    'Invalid file format. Only .jpg, .png, and .heif files are allowed.' | translate
                  }}
                </p>
              </div>
            </div>
          </div>
          <hr class="mt-2" />

          <h2 class="h2">{{ 'Dairy Location' | translate }}</h2>
          <div class="row">
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Dairy Name' | translate }}
                  <span class="required">*</span>
                </label>
                <!-- Show dairy location name in read-only mode if user is not owner -->
                <input
                  v-if="!isOwner && dairyLocation && dairyLocation.name"
                  type="text"
                  class="form-control"
                  :value="dairyLocation.name"
                  readonly
                />
                <!-- Show select dropdown if user is owner -->
                <select
                  v-else
                  :disabled="!isOwner"
                  class="form-control"
                  v-model="evaluation.dairyLocationId"
                  @change="changeDairyLocation()"
                  data-vv-as="Dairy Name"
                  name="dairy_name"
                  v-validate="'required'"
                >
                  <option disabled :value="null">
                    {{ 'Select' | translate }}
                  </option>
                  <option @selected="newDairyLocation" :value="recordSelection.MANUAL">
                    {{ 'Add New Dairy Location' | translate }}
                  </option>
                  <option
                    v-for="locationSelect in squadDairyLocations"
                    :key="locationSelect.id"
                    :value="locationSelect.id"
                  >
                    {{ locationSelect.name }}
                  </option>
                </select>
                <span v-show="isOwner && errors.has('dairy_name')" class="text-danger">
                  <small>{{ 'The Dairy Location field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-12">
              <div class="form-group" v-if="evaluation.dairyLocationId == recordSelection.MANUAL">
                <label class="mb-1"
                  >{{ 'Dairy Name' | translate }}
                  <span class="required">*</span>
                </label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  v-model="dairyLocationName"
                  data-vv-as="Dairy Name"
                  name="dairy_new_name"
                  v-validate="'required'"
                />
                <span v-show="isOwner && errors.has('dairy_new_name')" class="text-danger">
                  <small>{{ 'The Dairy Name field is required.' | translate }} </small>
                </span>
              </div>
            </div>
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Address 1' | translate }}
                  <span class="required">*</span>
                </label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  v-model="dairyLocationAddress"
                  name="address_1"
                  data-vv-as="Address 1"
                  v-validate="'required'"
                />
                <span v-show="isOwner && errors.has('address_1')" class="text-danger">
                  <small>{{ 'The Address 1 field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Country' | translate }}
                  <span class="required">*</span>
                </label>
                <select
                  v-if="dairyLocation"
                  :disabled="!isOwner"
                  @change="dairyLocation.state = null"
                  class="form-control"
                  v-model="dairyLocation.country"
                  name="dairy_country"
                  data-vv-as="Country"
                  v-validate="'required'"
                >
                  <option :value="null" disabled>
                    {{ 'Select' | translate }}
                  </option>
                  <option
                    v-for="(country, index) in $store.state.misc.countriesPrimary"
                    :key="country + index"
                    :value="index"
                  >
                    {{ country | translate }}
                  </option>
                  <option disabled>------------------</option>
                  <option
                    v-for="(country, index) in $store.state.misc.countries"
                    :key="index + country"
                    :value="index"
                  >
                    {{ country | translate }}
                  </option>
                </select>
                <span v-show="isOwner && errors.has('dairy_country')" class="text-danger">
                  <small>{{ 'The Country field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div v-if="dairyLocation" class="form-group">
                <label class="mb-1">
                  {{ 'State/Province' | translate }}
                </label>

                <select
                  v-if="dairyLocation && misc.states[dairyLocation.country]"
                  :disabled="!isOwner"
                  class="form-control"
                  v-model="dairyLocation.state"
                >
                  <template
                    v-if="
                      misc.states[dairyLocation.country] &&
                      misc.states[dairyLocation.country].primary &&
                      Object.keys(misc.states[dairyLocation.country].primary).length
                    "
                  >
                    <option :value="null" disabled>
                      {{ 'Select' | translate }}
                    </option>
                    <option
                      v-for="(state, abbr) in misc.states[dairyLocation.country].primary"
                      :key="state + abbr"
                      :value="abbr"
                    >
                      {{ state }}
                    </option>
                    <option disabled>------------------</option>
                  </template>
                  <option
                    v-for="(state, abbr) in misc.states[dairyLocation.country].regular"
                    :key="state"
                    :value="abbr"
                  >
                    {{ state }}
                  </option>
                </select>
                <input
                  v-else
                  v-model="dairyLocation.state"
                  type="text"
                  class="form-control"
                  :readonly="!isOwner"
                />
              </div>
            </div>
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1">{{ 'Address 2' | translate }}</label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  v-model="dairyLocation.address_line_2"
                />
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'City' | translate }}
                  <span class="required">*</span>
                </label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  v-model="dairyLocationCity"
                  name="city"
                  data-vv-as="City"
                  v-validate="'required'"
                />
                <span v-show="isOwner && errors.has('city')" class="text-danger">
                  <small>{{ 'The City field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1">{{ 'Postal Code' | translate }} </label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  v-model="dairyLocationPostal"
                  name="postal_code"
                  data-vv-as="Postal Code"
                />
                <span v-show="isOwner && errors.has('postal_code')" class="text-danger">
                  <small>{{ 'The Postal Code field is required.' | translate }}</small>
                </span>
              </div>
            </div>
          </div>

          <hr class="mt-2" />

          <h2 class="h2">{{ 'Contact' | translate }}</h2>

          <div class="row">
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Name' | translate }}
                  <span class="required">*</span>
                </label>
                <!-- Show contact name in read-only mode if user is not owner -->
                <input
                  v-if="!isOwner && contact && contact.name"
                  type="text"
                  class="form-control"
                  :value="contact.name"
                  readonly
                />
                <!-- Show select dropdown if user is owner -->
                <select
                  v-else
                  :disabled="!isOwner"
                  class="form-control"
                  v-model="evaluation.contactId"
                  data-vv-as="Contact Name"
                  @change="changeContact()"
                  v-validate="'required'"
                >
                  <option disabled :value="null">
                    {{ 'Select' | translate }}
                  </option>
                  <option @selected="newContact" :value="recordSelection.MANUAL">
                    {{ 'Add New Contact' | translate }}
                  </option>
                  <option
                    v-for="contactSelect in squadContacts"
                    :key="contactSelect.id"
                    :value="contactSelect.id"
                  >
                    {{ contactSelect && contactSelect.name }}
                  </option>
                </select>
                <span v-show="isOwner && errors.has('contact_name')" class="text-danger">
                  <small>{{ 'The Name field is required.' | translate }}</small>
                </span>
              </div>
            </div>

            <div class="offset-lg-12"></div>

            <div v-if="evaluation.contactId === recordSelection.MANUAL" class="col-lg-12">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Name' | translate }}
                  <span class="required">*</span>
                </label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  v-model="contactName"
                  name="last_name"
                  data-vv-as="Last Name"
                  v-validate="'required'"
                />
                <span v-show="isOwner && errors.has('last_name')" class="text-danger">
                  <small>{{ 'The Name field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Email 1' | translate }}
                  <span class="required">*</span>
                </label>
                <input
                  :readonly="!isOwner"
                  type="email"
                  class="form-control"
                  v-model="contactEmail"
                  name="email_address_1"
                  data-vv-as="Email 1"
                  v-validate="'required|email'"
                />
                <span v-show="isOwner && errors.has('email_address_1')" class="text-danger">
                  <small>{{ 'The Email 1 field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1">{{ 'Email 2' | translate }}</label>
                <input
                  :readonly="!isOwner"
                  type="email"
                  class="form-control"
                  v-model="(contact || {}).email_2"
                />
              </div>
            </div>
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1">{{ 'Title' | translate }}</label>
                <input
                  :readonly="!isOwner"
                  type="text"
                  class="form-control"
                  v-model="(contact || {}).title"
                />
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Business Phone' | translate }}
                  <span class="required">*</span>
                </label>
                <input
                  :readonly="!isOwner"
                  type="tel"
                  class="form-control"
                  v-model="contactBusiness"
                  name="business_phone"
                  data-vv-as="Business Phone"
                  v-validate="'required'"
                />
                <span v-show="isOwner && errors.has('business_phone')" class="text-danger">
                  <small>{{ 'The Business Phone field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1">{{ 'Mobile' | translate }}</label>
                <input
                  :readonly="!isOwner"
                  type="tel"
                  class="form-control"
                  v-model="(contact || {}).mobile_phone"
                />
              </div>
            </div>

            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1">{{ 'Other' | translate }}</label>
                <input
                  :readonly="!isOwner"
                  type="tel"
                  class="form-control"
                  v-model="(contact || {}).other"
                />
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1">{{ 'Fax' | translate }}</label>
                <input
                  :readonly="!isOwner"
                  type="tel"
                  class="form-control"
                  v-model="(contact || {}).fax"
                />
              </div>
            </div>
          </div>

          <hr class="mt-2" />

          <h2 class="h2">{{ 'Characteristics' | translate }}</h2>
          <div class="row">
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Consultation Date' | translate }}
                  <span class="required">*</span>
                </label>
                <vuejs-datepicker
                  v-on:input="convertDate"
                  :value="evaluation.data.consultation_date"
                  :typeable="true"
                  input-class="form-control date-picker"
                  data-vv-as="Consultation Date"
                  name="consultation_date"
                  v-model="evaluation.data.consultation_date"
                  v-validate="'required'"
                  :format="formatDate"
                  :language="selectedLanguage"
                ></vuejs-datepicker>

                <span v-show="isOwner && errors.has('consultation_date')" class="text-danger">
                  <small>{{ 'The Consultation Date field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Herd Size' | translate }}
                  <span class="required">*</span>
                </label>
                <currency-input
                  key="herd_size"
                  name="herd_size"
                  v-model="evaluation.data.herdSize"
                  data-vv-as="Herd Size"
                  v-validate="'required'"
                  :options="{
                    currency: 'USD',
                    precision: 0,
                  }"
                />
                <div v-show="errors.has('herd_size')" class="text-danger">
                  <small>{{ 'The Herd Size field is required.' | translate }}</small>
                </div>
              </div>
            </div>
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1">{{ 'Nutritionist' | translate }}</label>
                <input
                  type="text"
                  class="form-control"
                  name="nutritionist"
                  v-model="evaluation.data.nutritionist"
                />
              </div>
            </div>
            <measurement-selection-radio v-model="evaluation.data.measurements" />
            <!-- start -->

            <div class="col-lg-6">
              <div class="form-group">
                <label class="d-block"
                  >{{
                    'Does this evaluation contain false data and not actual real-world information?'
                      | translate
                  }}
                  <span class="required">*</span>
                </label>
                <div class="form-check form-check-inline">
                  <label class="form-check-label">
                    <input
                      class="form-check-input"
                      type="radio"
                      name="is_for_testing"
                      :value="true"
                      data-vv-as="Is For Testing"
                      v-validate="'required'"
                      v-model="evaluation.isForTesting"
                    />
                    {{ 'Yes' | translate }}
                  </label>
                </div>
                <div class="form-check form-check-inline">
                  <label class="form-check-label">
                    <input
                      class="form-check-input"
                      type="radio"
                      name="is_for_testing"
                      :value="false"
                      data-vv-as="Is For Testing"
                      v-validate="'required'"
                      v-model="evaluation.isForTesting"
                    />
                    {{ 'No' | translate }}
                  </label>
                </div>
                <span v-if="errors.has('is_for_testing')" class="text-danger d-block">
                  <small>{{ 'Please fill in required fields' | translate }}</small>
                </span>
              </div>
            </div>
            <!-- end -->
            <div class="col-lg-12">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Currency' | translate }}
                  <span class="required">*</span>
                </label>
                <select
                  name="currency"
                  class="form-control"
                  v-model="evaluation.data.currency"
                  data-vv-as="Currency"
                  v-validate="'required'"
                >
                  <option disabled :value="null">
                    {{ 'Select' | translate }}
                  </option>
                  <option
                    v-for="(currency, index) in $store.state.misc.currencies"
                    :key="index"
                    :value="index"
                  >
                    {{ currency | translate }}
                  </option>
                </select>
                <span v-show="isOwner && errors.has('currency')" class="text-danger">
                  <small>{{ 'The Currency field is required.' | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1">{{ 'Veterinarian' | translate }}</label>
                <input type="text" class="form-control" v-model="evaluation.data.veterinarian" />
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1">
                  {{
                    $t('Milk Production') +
                    ' (' +
                    $t($getEquivalentUnit(evaluation.data.measurements, 'kg')) +
                    $t('/day)')
                  }}
                  <span class="required">*</span>
                </label>
                <currency-input
                  key="computed_milk_production"
                  name="computed_milk_production"
                  v-model="computed_milk_production"
                  data-vv-as="Milk Production"
                  v-validate="'required'"
                  :options="{
                    currency: 'USD',
                    precision: { min: 0, max: 2 },
                  }"
                />
                <span v-show="errors.has('computed_milk_production')" class="text-danger d-block">
                  <small>{{ errors.first('computed_milk_production') | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1"
                  >{{ 'Milk Price' | translate }}
                  <span>
                    {{
                      ('(' +
                        computed_currency +
                        ')' +
                        ' / ' +
                        $t($getEquivalentUnit(evaluation.data.measurements, 'kg')))
                        | translate
                    }}
                    <span class="required">*</span>
                  </span>
                </label>
                <currency-input
                  key="computed_milk_price"
                  name="computed_milk_price"
                  v-model="computed_milk_price"
                  :data-vv-as="$t('Milk Price')"
                  v-validate="'required'"
                  :options="{
                    currency: 'USD',
                    precision: { min: 0, max: 2 },
                  }"
                />
                <span v-show="errors.has('computed_milk_price')" class="text-danger d-block">
                  <small>{{ errors.first('computed_milk_price') | translate }}</small>
                </span>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-group">
                <label class="mb-1">{{ 'Hoof Trimmer' | translate }}</label>
                <input
                  type="text"
                  class="form-control"
                  name="trimmer"
                  v-model="evaluation.data.trimmer"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="requiredFieldsMessage === true"
          class="alert alert-danger mt-4 mb-0"
          role="alert"
        >
          {{ 'Please fill out the required fields!' | translate }}
        </div>

        <div v-if="errorMessage === true" class="alert alert-danger mt-4 mb-0" role="alert">
          {{ 'Sorry! Something went wrong.' | translate }}
        </div>

        <div class="row d-flex">
          <div class="order-2 order-md-1 col-md-12">
            <router-link to="/" class="btn btn-secondary btn-full--sm mt-2 mt-md-4">{{
              'Back' | translate
            }}</router-link>
          </div>
          <div class="order-1 order-md-2 col-md-12">
            <button
              @click="nextStep"
              class="btn btn-primary btn-full--sm mt-4 float-right"
              :disabled="isSaving || loader"
            >
              {{ 'Next' | translate }}
              <span v-if="loader === true" class="loader ml-1"></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import vuejsDatepicker from 'vuejs-datepicker';

import {
  GET_EVALUATION_BY_ID,
  GET_DAIRY_LOCATION_BY_CLIENT_ID_GETTER,
  GET_CONTACT_BY_CLIENT_ID_GETTER,
  GET_ADD_EVALUATION_IN_DB,
  GET_ADD_CLIENT_INTO_DB,
  GET_UPDATE_CLIENT,
  GET_CLIENT_BY_ID_GETTER,
  GET_UPDATE_EVALUATION,
  SET_EVALUATION_BY_ID,
  GET_EVALUATION_BY_ID_GETTER,
  GET_USERS_BY_ID_GETTER,
  SET_ADD_EVALUATION_IN_DB,
  GET_CLIENTS,
} from '../../store/types';

import { cloneDeep } from 'lodash';
import { newObjectId } from '@/helperFunctions.js';
import { RecordSelection } from '@/constants';
import imageUploadMixin from '@/mixins/imageUploadFn.js';
import Evaluation from '@/libs/Classes/Evaluation.js';
import MeasurementSelectionRadio from '@/components/common/MeasurementSelectionRadio.vue';
import IndicatorForTestingEvaluation from '@/components/common/IndicatorForTestingEvaluation.vue';
import { zh, fr, es, en, de, ptBR, pl, it, ru, nl } from 'vuejs-datepicker/dist/locale';
import CurrencyInput from '@/components/common/CurrencyInput.vue';

export default {
  components: {
    vuejsDatepicker,
    MeasurementSelectionRadio,
    IndicatorForTestingEvaluation,
    CurrencyInput,
  },
  mixins: [imageUploadMixin],

  data() {
    return {
      user: {},
      fileSizeError: false,
      fileFormatError: false,
      ch: zh,
      fr,
      es,
      en,
      de,
      pt: ptBR,
      pl,
      it,
      ru,
      nl,
      isSaving: false,
      loaded: false,
      dairyLocations: [],
      contacts: [],
      contactBusiness: null,
      contactEmail: null,
      clientName: null,
      clientCity: null,
      clientAddress: null,
      clientPostal: null,
      contactName: null,
      dairyLocationAddress: null,
      dairyLocationCity: null,
      dairyLocationName: null,
      dairyLocationPostal: null,
      errorMessage: false,
      requiredFieldsMessage: false,
      evaluation: null,
      evaluationClass: null,
      groupClass: null,
      herdCharacteristics: null,
      isCheckAll: false,
      isLoaded: false,
      loader: false,
      default_evaluation: {
        id: this.$generateId(),
        name: null,
        userId: null,
        dairyLocationId: null,
        contactId: null,
        clientId: null,
        clientData: null,
        contacts: [],
        groups: {},
        mixMeasurements: null,
        isForTesting: null,
        data: {
          currency: null,
          measurements: 'metric', // default value
          herdSize: null,
        },
        report: {
          assessors: [],
          groups: [],
          file_name: 'Report 1', // Not sure if needed
          file_size: 234444, // Not sure if needed
          executive_summary_title: null,
          executive_summary_content: null,
          output_data: {}, // object
          date_created: 1527795454,
        },
        date_created: 1527795454,
        date_updated: 1527795454,
      },
      client: {
        id: this.$generateId(),
        country: null,
        state: null,
        name: null,
        address_2: null,
      },
      dairyLocation: {
        id: this.$generateId(),
        country: null,
        state: null,
      },
      contact: {
        id: this.$generateId(),
        email_2: null,
        title: null,
        mobile_phone: null,
        other: null,
        fax: null,
      },
      imperialMilkProduction: null,
      metricMilkProduction: null,
      hiddenImperialMilkProduction: null,
      hiddenMetricMilkProduction: null,
      originalMeasurement: null,
      userEnteredDecimal: false,
      isEditMode: false,
      clientsLoading: false,
    };
  },

  created() {
    // Fetch the owner's companies (clients) directly instead of relying on
    // App.vue's root-level mounted() dispatch, which fires only after this
    // page has already mounted and can silently fail with no retry.
    this.clientsLoading = true;
    this[GET_CLIENTS]()
      .catch((err) => {
        console.log(err, 'error fetching clients in HerdCharacteristics');
      })
      .finally(() => {
        this.clientsLoading = false;
      });
  },

  mounted() {
    setTimeout(() => {
      const { userId: paramUserId } = this.$route.params;
      const userId = paramUserId ? paramUserId : this.userId;
      this.user = this[GET_USERS_BY_ID_GETTER](userId);
      // setTimeout(() => {
      if (this.userId && paramUserId) {
        this.editTheEvaluation();
      } else {
        this.initializeNewEvaluation();
      }
      // }, 1000);

      if (!this.evaluation.data.measurements) {
        this.evaluation.data.measurements = this.user.measurements;
        this.evaluation.data.mixMeasurements = this.user.measurements;
      }

      if (!this.evaluation.data.currency) {
        this.evaluation.data.currency = this.user.currency;
      }
      // Initialize milk production values if they exist
      // if (this.evaluation.data.milkProduction) {
      //   this.hiddenImperialMilkProduction = this.evaluation.data.milkProduction;
      //   this.hiddenMetricMilkProduction = this.evaluation.data.milkProduction;
      //   this.updateMilkProduction();
      // }

      if (this.evaluation.data.measurements === 'imperial') {
        this.imperialMilkProduction = this.evaluation.data.imperialMilkProduction != null ? this.evaluation.data.imperialMilkProduction : Math.round(this.evaluation.data.milkProduction);
        this.hiddenImperialMilkProduction = this.evaluation.data.imperialMilkProduction != null ? this.evaluation.data.imperialMilkProduction : this.evaluation.data.milkProduction;
        this.hiddenMetricMilkProduction = this.evaluation.data.metricMilkProduction != null ? this.evaluation.data.metricMilkProduction : this.evaluation.data.milkProduction * 0.45359237;
        this.metricMilkProduction = Math.round(this.hiddenMetricMilkProduction);
      } else {
        this.metricMilkProduction = this.evaluation.data.metricMilkProduction != null ? this.evaluation.data.metricMilkProduction : Math.round(this.evaluation.data.milkProduction);
        this.hiddenMetricMilkProduction = this.evaluation.data.metricMilkProduction != null ? this.evaluation.data.metricMilkProduction : this.evaluation.data.milkProduction;
        this.hiddenImperialMilkProduction = this.evaluation.data.imperialMilkProduction != null ? this.evaluation.data.imperialMilkProduction : this.evaluation.data.milkProduction / 0.45359237;
        this.imperialMilkProduction = Math.round(this.hiddenImperialMilkProduction);
      }
      this.updateMilkProduction();

      this.evaluationClass = new Evaluation(this, this.evaluation);

      // Check if it's an edit mode (you might need to adjust this condition based on your app's logic)
      this.isEditMode = !!this.evaluation.id; // or any other condition that determines if it's an edit
      this.isLoaded = true;
    }, 1000);
  },
  computed: {
      // Exposes the shared dropdown sentinels to the template so the "Add New ..."
      // options bind to them instead of repeating the raw string.
      recordSelection() {
        return RecordSelection;
      },
    ...mapState({
      evaluations: (state) => state.evaluations.evaluations,
      userId: (state) => state.users.user._id,
      // user: state => state.users.user,
      clients: (state) => state.clients.clients,
      assessorTypes: (state) => state.assessorTypes,
      misc: (state) => state.misc,
      lang: (state) => state.i18n.locale,
    }),

    ...mapGetters('evaluations', [GET_EVALUATION_BY_ID_GETTER]),
    ...mapGetters('users', [GET_USERS_BY_ID_GETTER]),

    ...mapGetters('clients', [
      GET_DAIRY_LOCATION_BY_CLIENT_ID_GETTER,
      GET_CONTACT_BY_CLIENT_ID_GETTER,
      GET_CLIENT_BY_ID_GETTER,
    ]),

    selectedLanguage() {
      return this[this.lang];
    },

    // Check if current user is the owner of the evaluation
    isOwner() {
      return this.evaluation && this.evaluation.userId === this.userId;
    },

    clientImgSrc() {
      console.log(this.client.brandingImageBase64.startsWith('data:image'));
      return this.client.brandingImageBase64.startsWith('data:image')
        ? this.dataURItoBlob(this.client.brandingImageBase64)
        : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    },

    squadClients() {
      const clientsAsArray = Object.values(this.clients || {}) || [];

      return clientsAsArray.filter((client) => typeof client.name !== 'undefined') || [];
    },

    squadDairyLocations() {
      const dairyLocationsAsArray = Object.values(this.dairyLocations || {}) || [];

      return dairyLocationsAsArray.filter((location) => location && location.name) || [];
    },

    squadContacts() {
      const contactsAsArray = Object.values(this.contacts || {}) || [];
      return contactsAsArray.filter((contact) => contact && contact.name) || [];
    },

    showAllFields() {
      if (this.evaluation.clientId === null) return false;
      // If user is not owner, only show fields in read-only mode
      return this.isOwner;
    },

    showDairyFields() {
      if (this.evaluation.clientId === null) return false;
      // If user is not owner, only show fields in read-only mode
      return this.isOwner;
    },

    showContactFields() {
      if (this.evaluation.clientId === null) return false;
      // If user is not owner, only show fields in read-only mode
      return this.isOwner;
    },

    showClientFields() {
      // If user is not owner, only show fields in read-only mode
      return this.isOwner;
    },

    computed_milk_price: {
      cache: false,
      get() {
        return this.evaluationClass.getMilkPrice(false, true);
      },
      set(newValue) {
        return this.evaluationClass.setMilkPrice(newValue);
      },
    },

    displayMilkProduction: {
      get() {
        console.log(
          '🚀 ~ get ~ get:',
          this.evaluation.data.showMilkProduction,
          this.imperialMilkProduction,
          this.evaluation.data.measurements,
        );

        if (this.evaluation.data.measurements === 'imperial') {
          return this.imperialMilkProduction !== 0 || null
            ? this.imperialMilkProduction
            : this.evaluation.data.showMilkProduction;
        } else {
          return this.metricMilkProduction !== 0 || null
            ? this.metricMilkProduction
            : this.evaluation.data.showMilkProduction;
        }
      },
      set(value) {
        this.userEnteredDecimal = value % 1 !== 0;
        if (this.evaluation.data.measurements === 'imperial') {
          this.imperialMilkProduction = value;
          this.hiddenImperialMilkProduction = value;
          this.hiddenMetricMilkProduction = value * 0.45359237;
          this.metricMilkProduction = Math.round(this.hiddenMetricMilkProduction);
        } else {
          this.metricMilkProduction = value;
          this.hiddenMetricMilkProduction = value;
          this.hiddenImperialMilkProduction = value / 0.45359237;
          this.imperialMilkProduction = Math.round(this.hiddenImperialMilkProduction);
        }
        this.originalMeasurement = this.evaluation.data.measurements;
        this.evaluationClass.setMilkProduction(value);
      },
    },

    computed_milk_production: {
      cache: false,
      get() {
        return this.evaluationClass.getInitiallMilkProduction(false);
      },
      set(newValue) {
        return this.evaluationClass.setInitiallMilkProduction(newValue);
      },
    },

    computed_currency() {
      if (this.evaluation.data.currency === null) {
        return 'currency';
      } else {
        return this.evaluation.data.currency;
      }
    },
  },

  watch: {
    evaluations: {
      deep: true,
      handler(newVals) {
        if (!this.evaluation || !this.evaluation._id) return;
        
        const latestEvaluation = newVals.find(e => e._id === this.evaluation._id);
        if (latestEvaluation) {
          const oldProduction = this.evaluation.data.milkProduction;
          const oldImperial = this.evaluation.data.imperialMilkProduction;
          const oldMetric = this.evaluation.data.metricMilkProduction;
          
          this.evaluation = cloneDeep(latestEvaluation);
          
          if (oldProduction !== latestEvaluation.data.milkProduction || oldImperial !== latestEvaluation.data.imperialMilkProduction) {
            if (this.evaluation.data.measurements === 'imperial') {
              this.imperialMilkProduction = this.evaluation.data.imperialMilkProduction != null ? this.evaluation.data.imperialMilkProduction : Math.round(this.evaluation.data.milkProduction);
              this.hiddenImperialMilkProduction = this.evaluation.data.imperialMilkProduction != null ? this.evaluation.data.imperialMilkProduction : this.evaluation.data.milkProduction;
              this.hiddenMetricMilkProduction = this.evaluation.data.metricMilkProduction != null ? this.evaluation.data.metricMilkProduction : this.evaluation.data.milkProduction * 0.45359237;
              this.metricMilkProduction = Math.round(this.hiddenMetricMilkProduction);
            } else {
              this.metricMilkProduction = this.evaluation.data.metricMilkProduction != null ? this.evaluation.data.metricMilkProduction : Math.round(this.evaluation.data.milkProduction);
              this.hiddenMetricMilkProduction = this.evaluation.data.metricMilkProduction != null ? this.evaluation.data.metricMilkProduction : this.evaluation.data.milkProduction;
              this.hiddenImperialMilkProduction = this.evaluation.data.imperialMilkProduction != null ? this.evaluation.data.imperialMilkProduction : this.evaluation.data.milkProduction / 0.45359237;
              this.imperialMilkProduction = Math.round(this.hiddenImperialMilkProduction);
            }
            this.updateMilkProduction();
          }
        }
      }
    },
    'evaluation.data.measurements': {
      handler() {
        this.updateMilkProduction();
      },
    },
    clientName(val) {
      this.client.name = val;
    },
    clientCity(val) {
      this.client.city = val;
    },
    clientAddress(val) {
      this.client.address_1 = val;
    },
    clientPostal(val) {
      this.client.postal_code = val;
    },
    dairyLocationName(val) {
      this.dairyLocation.name = val;
    },
    dairyLocationAddress(val) {
      this.dairyLocation.address_1 = val;
    },
    dairyLocationCity(val) {
      this.dairyLocation.city = val;
    },
    dairyLocationPostal(val) {
      this.dairyLocation.postal_code = val;
    },
    contactName(val) {
      this.contact.name = val;
    },
    contactEmail(val) {
      this.contact.email_1 = val;
    },
    contactBusiness(val) {
      this.contact.business_phone = val;
    },
  },

  methods: {
    ...mapActions('evaluations', [
      GET_EVALUATION_BY_ID,
      GET_ADD_EVALUATION_IN_DB,
      GET_UPDATE_EVALUATION,
    ]),
    ...mapActions('clients', [GET_ADD_CLIENT_INTO_DB, GET_UPDATE_CLIENT, GET_CLIENTS]),

    async fileUpload(e, objectToUpdate) {
      // Getting the files from the vue upload
      var files = e.target.files;
      // Getting the first file
      var getFile = files[0];

      // Your existing size and format validation
      if (getFile.size / 1024 / 1024 > 5) {
        this.fileSizeError = true;
        setTimeout(() => {
          this.fileSizeError = false;
        }, 5000);
        this.resetFileInput();
        return;
      }
      const allowedFormats = ['image/jpeg', 'image/png', 'image/heif'];
      if (!allowedFormats.includes(getFile.type)) {
        this.fileFormatError = true;
        setTimeout(() => {
          this.fileFormatError = false;
        }, 5000);
        this.resetFileInput();
        return;
      }
      var url = URL.createObjectURL(getFile);

      console.log(url);

      var self = this;

      const { dataUrl, height, width } = await this.getBase64WithWidthAndHeight(url);
      self[objectToUpdate].brandingImageBase64 = dataUrl;
      self[objectToUpdate].brandingImageHeight = height;
      self[objectToUpdate].brandingImageWidth = width;
      // this.imgUrls.push(url);
      this.toDataURL(url, function (dataUrl) {
        console.log({ dataUrl });
        self[objectToUpdate].brandingImageBase64 = dataUrl;
        self.$forceUpdate();
      });
    },

    // Add this new method to your component
    resetFileInput() {
      // Reset the file input
      this.$refs.fileInput.value = '';
    },

    formatDate() {
      return this.$store.getters.getDate(this.evaluation.data.consultation_date);
    },
    updateMilkProduction() {
      if (this.evaluation.data.measurements === 'imperial') {
        if (this.originalMeasurement === 'metric' || !this.userEnteredDecimal) {
          this.imperialMilkProduction = Math.round(this.hiddenImperialMilkProduction);
        } else {
          this.imperialMilkProduction = this.hiddenImperialMilkProduction;
        }
        this.metricMilkProduction = Math.round(this.hiddenMetricMilkProduction);
      } else {
        if (this.originalMeasurement === 'imperial' || !this.userEnteredDecimal) {
          this.metricMilkProduction = Math.round(this.hiddenMetricMilkProduction);
        } else {
          this.metricMilkProduction = this.hiddenMetricMilkProduction;
        }
        this.imperialMilkProduction = Math.round(this.hiddenImperialMilkProduction);
      }
    },

    initializeNewEvaluation() {
      // if evaluationId is not set, then we are creating a new evaluation
      this.evaluation = cloneDeep(this.default_evaluation);
      this.evaluation.data.consultation_date = new Date().getTime();
      this.evaluation.date_created = new Date().getTime();
      this.evaluation.date_updated = new Date().getTime();
      this.evaluation.userId = this.userId;
    },
    async editTheEvaluation() {
      console.log('edit evaluation');
      const { userId: paramUserId, evaluationId } = this.$route.params;
      this.evaluation = this[GET_EVALUATION_BY_ID_GETTER](evaluationId);
      // this.evaluation.userId = paramUserId; // set the evaluation's userId just in case it is not set already
      this.evaluation.isForTesting = this.evaluation.isForTesting ?? null;

      if (!this.evaluation) {
        this.$router.push(`/users/${this.userId}/evaluations?errorCode=evaluation/not-found`);
      }
      if (this.evaluation.clientId) {
        this.client = this.evaluation.clientData;
        if (this.client) {
          this.clientName = this.client.name;
          this.clientCity = this.client.city;
          this.clientAddress = this.client.address_1;
          this.clientPostal = this.client.postal_code;
        }

        this.dairyLocations = this.client.dairy_locations;
        this.changeDairyLocation();

        this.contacts = this.client.contacts;
        this.changeContact();
        if (this.contact) {
          this.contactName = this.contact.name;
          this.contactEmail = this.contact.email_1;
          this.contactBusiness = this.contact.business_phone;
        }
        // check if evaluation doesn't contain the mixedMeasurement property, add it
        if (!this.evaluation.data.mixMeasurements) {
          this.evaluation.data.mixMeasurements = this.user.measurements;
        }

        this.dairyLocationName = this.dairyLocation.name;
        this.dairyLocationAddress = this.dairyLocation.address_1;
        this.dairyLocationCity = this.dairyLocation.city;
        this.dairyLocationPostal = this.dairyLocation.postal_code;
      }
    },

    getClientById(id) {
      return this[GET_CLIENT_BY_ID_GETTER](id);
    },

    getContactById(clientId, id) {
      this[GET_CONTACT_BY_CLIENT_ID_GETTER](clientId, id);
    },

    async nextStep() {
      try {
        if (this.isSaving) return;
        this.isSaving = true;
        this.loader = true;

        const { userId = this.userId } = this.$route.params;

        this.$validator.validateAll().then(async (result) => {
          // @TODO Refactor this *validation*
          let validationResult = result;

          // If user is not owner, skip validation for company, dairy location, and contact fields
          if (!this.isOwner) {
            // Clear validation errors only for read-only fields that non-owners can't edit
            this.clearReadOnlyFieldErrors();
            // Re-validate only the fields that are always required regardless of ownership
            validationResult = this.validateAlwaysRequiredFields();
          }

          // Always validate these fields regardless of ownership
          if (
            !this.computed_milk_price ||
            !this.computed_milk_production ||
            !this.evaluation.name ||
            !this.evaluation.data.herdSize ||
            this.evaluation.isForTesting === null
          ) {
            validationResult = false;
          }

          if (validationResult === true) {
            if (!this.evaluations.length) {
            }

            // Handle client data based on ownership and availability
            let clientData = null;
            if (this.isOwner) {
              // Only create client if we have the necessary data
              if (this.contact && this.contact.id && this.dairyLocation && this.dairyLocation.id) {
                clientData = this.createClient();
              }
            } else {
              // Use existing client data for non-owners if available
              clientData = this.evaluation.clientData || null;
            }

            // Build evaluation object with conditional properties
            const evaluationData = {
              _id: newObjectId(),
              ...this.evaluation,
            };

            // Only add contactId if contact exists and has an id
            if (this.contact && this.contact.id) {
              evaluationData.contactId = this.contact.id;
            }

            // Only add dairyLocationId if dairyLocation exists and has an id
            if (this.dairyLocation && this.dairyLocation.id) {
              evaluationData.dairyLocationId = this.dairyLocation.id;
            }

            // Only add clientId and clientData if clientData exists
            if (clientData && clientData._id) {
              evaluationData.clientId = clientData._id;
              evaluationData.clientData = clientData;
            }

            this.evaluation = evaluationData;

            if (!this.evaluation.data.mixMeasurements) {
              this.evaluation.data.mixMeasurements = this.evaluation.data.measurements;
            }

            // Update the evaluation object with the hidden values for calculations
            /* this.evaluation.data.milkProduction =
              this.evaluation.data.measurements === 'imperial'
              ? this.imperialMilkProduction
              : this.metricMilkProduction;
            
            this.evaluation.data.showMilkProduction = this.displayMilkProduction */

            const { evaluationId: updateTheEvaluation } = this.$route.params;

            this.$store.commit(`evaluations/${SET_ADD_EVALUATION_IN_DB}`, this.evaluation);

            this[updateTheEvaluation ? GET_UPDATE_EVALUATION : GET_ADD_EVALUATION_IN_DB](
              this.evaluation,
            );

            this.loader = false;

            // The Next button sits at the foot of a long form and the router keeps the
            // window offset across navigations, so without this the next screen opens
            // already scrolled to the bottom.
            window.scrollTo(0, 0);

            this.$router.push({
              path: `/users/${this.userId}/evaluations/${this.evaluation._id}/groups`,
            });
          } else {
            this.requiredFieldsMessage = true;
            this.loader = false;
            this.isSaving = false;
            setTimeout(() => {
              this.requiredFieldsMessage = false;
            }, 5000);
          }
        });
      } catch (error) {
        this.isSaving = false;
        console.log(error);
      }
    },

    // Clear validation errors for read-only fields that non-owners can't edit
    clearReadOnlyFieldErrors() {
      // List of field names that are read-only for non-owners
      const readOnlyFields = [
        'company',
        'company_name',
        'company_address_line_1',
        'company_address_line_2',
        'company_city',
        'company_state_province',
        'company_zip_postal_code',
        'company_country',
        'dairy_name',
        'dairy_address_line_1',
        'dairy_address_line_2',
        'dairy_city',
        'dairy_state_province',
        'dairy_zip_postal_code',
        'dairy_country',
        'contact_name',
        'last_name',
        'email_address_1',
        'business_phone',
      ];

      // Clear errors for read-only fields
      readOnlyFields.forEach((fieldName) => {
        this.$validator.errors.remove(fieldName);
      });
    },

    // Validate only the fields that are always required regardless of ownership
    validateAlwaysRequiredFields() {
      // These fields are always required and should be validated for all users
      const alwaysRequiredFields = ['evaluation_name', 'is_for_testing'];

      let isValid = true;

      // Check evaluation name
      if (!this.evaluation.name || this.evaluation.name.trim() === '') {
        this.$validator.errors.add({
          field: 'evaluation_name',
          msg: this.$t('The Evaluation Name field is required.'),
        });
        isValid = false;
      }

      // Check isForTesting
      if (this.evaluation.isForTesting === null || this.evaluation.isForTesting === undefined) {
        this.$validator.errors.add({
          field: 'is_for_testing',
          msg: this.$t('The Is For Testing field is required.'),
        });
        isValid = false;
      }

      // Check milk price and production (these are computed values)
      if (
        !this.computed_milk_price ||
        !this.computed_milk_production ||
        !this.evaluation.data.herdSize
      ) {
        isValid = false;
      }

      return isValid;
    },
    createClient() {
      try {
        const isCreateNew = this.evaluation.clientId === RecordSelection.MANUAL;

        const clientWithAllData = {
          _id: newObjectId(),
          ...this.client,
          contacts: {
            ...this.contacts,
            [this.contact.id]: this.contact,
          },
          dairy_locations: {
            ...this.dairyLocations,
            [this.dairyLocation.id]: this.dairyLocation,
          },
        };

        // TODO: Need to verify that on update user should save contact and dairy location proper data.
        if (isCreateNew) this[GET_ADD_CLIENT_INTO_DB](clientWithAllData);
        else this[GET_UPDATE_CLIENT](clientWithAllData);

        return clientWithAllData;
      } catch (error) {
        throw error;
      }
    },
    newClient() {
      this.client = {
        userId: this.userId,
        country: null,
        state: null,
      };
      this.dairyLocations = {
        userId: this.userId,
      };
      this.contacts = {
        userId: this.userId,
      };
      this.client.id = this.$generateId();
      this.$validator.reset();
    },
    convertDate() {
      this.evaluation.data.consultation_date = new Date(
        this.evaluation.data.consultation_date,
      ).getTime();
    },
    newDairyLocation() {
      this.dairyLocation = {
        id: this.$generateId(),
        name: null,
        address_1: null,
        address_2: null,
        country: null,
        state: null,
        city: null,
        postal_code: null,
        date_created: null,
        date_updated: null,
        userId: this.userId,
      };
      this.dairyLocationName = null;
      this.dairyLocationAddress = null;
      this.dairyLocationCity = null;
      this.dairyLocationPostal = null;

      this.$validator.reset();
    },
    newContact() {
      this.contact = {
        id: this.$generateId(),
        title: null,
        email_1: null,
        email_2: null,
        business_phone: null,
        mobile_phone: null,
        other: null,
        fax: null,
        date_created: null,
        date_updated: null,
        userId: this.userId,
      };
      this.contactName = null;
      this.contactEmail = null;
      this.contactBusiness = null;

      this.$validator.reset();
    },
    changeClient() {
      this.evaluation.dairyLocationId = RecordSelection.MANUAL;
      this.evaluation.contactId = RecordSelection.MANUAL;

      if (this.evaluation.clientId === RecordSelection.MANUAL) {
        this.newClient();
        this.newDairyLocation();
        this.newContact();
      } else {
        const client = this.getClientById(this.evaluation.clientId);
        if (client) {
          this.client = cloneDeep(client);
        }

        this.dairyLocations = this.client.dairy_locations;

        this.contacts = this.client.contacts;
        this.contact = this.client.contacts[this.evaluation.contactId];
        this.newDairyLocation();
        this.newContact();
      }
      this.clientName = this.client.name;
      this.clientCity = this.client.city;
      this.clientAddress = this.client.address_1;
      this.clientPostal = this.client.postal_code;

      this.$validator.reset();
    },
    changeDairyLocation() {
      this.$validator.pause();
      if (this.evaluation.dairyLocationId === '') {
        return;
      }
      if (this.evaluation.dairyLocationId == RecordSelection.MANUAL) {
        this.newDairyLocation();
      } else if (this.dairyLocations) {
        this.dairyLocation = this.dairyLocations[this.evaluation.dairyLocationId];
        this.dairyLocationName = this.dairyLocation.name;
        this.dairyLocationAddress = this.dairyLocation.address_1;
        this.dairyLocationCity = this.dairyLocation.city;
        this.dairyLocationPostal = this.dairyLocation.postal_code;
      }
      this.$validator.reset();
    },
    changeContact() {
      if (this.evaluation.contactId === '') {
        return;
      }
      if (this.evaluation.contactId == RecordSelection.MANUAL) {
        this.newContact();
      } else {
        const contact = this.contacts[this.evaluation.contactId];
        if (contact) {
          this.contact = contact;
          this.contactName = contact.name;
          this.contactEmail = contact.email_1;
          this.contactBusiness = contact.business_phone;
        }
      }
      this.$validator.reset();
    },

    getMilkProduction() {
      return this.milkProductionInput !== null
        ? this.milkProductionInput
        : this.evaluationClass.getInitiallMilkProduction(false);
    },
    //Upload a photo to the local
  },
};
</script>

<style scoped>
.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

select:disabled {
  background-color: #e9ecef;
  opacity: 1;
}
</style>